import { SoapEntryTypeNames } from '@patient/store/entry/Entry';

import { store } from '@store';
import { ENTRY_MEDICAL_CASE_BLOCK_ID } from '@store/types';
import { EntryTypeSection } from '@template/store/entryType/EntryTypeSection';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { EntryTypeFieldName, EntryTypeSectionData, Sex } from 'chaika-frontend-types/frontend-cms';

class StaticEntryTypeService {
    private exludeInSoapFieldNames = new Set<string>();
    public getObjectiveSections(sex: Sex): EntryTypeSection[] {
        return store.entry.entryType.getSectionsBySex(sex);
    }

    public getSectionsByTabName(tabName: SoapEntryTypeNames, sex: Sex): EntryTypeSection[] {
        const { typeId, soapEntryType } = store.entry;
        const sections = soapEntryType.get(tabName)?.getSectionsBySex(sex) ?? [];
        return sections.filter((section: EntryTypeSection) => section.isAllowForEntryType(typeId));
    }

    public addFilledSoapSections(sections: EntryTypeSectionData[]): EntryTypeSectionData[] {
        const entryFields = store.entry.fields;

        const soapSections = toArrayFromIterable(store.entry.soapEntryType).flatMap(
            entryType => entryType.data?.sections ?? [],
        );
        const soapFieldsNames = this.getSoapFieldNames();
        const fieldsNamesWithValue = soapFieldsNames.filter(name => entryFields.hasValue(name));

        const existFieldsNames = new Set<string>(
            sections.flatMap(({ blocks }) => blocks.flatMap(({ fields }) => fields.flatMap(({ name }) => name))),
        );
        const fieldsToAdd = fieldsNamesWithValue.filter(name => !existFieldsNames.has(name));

        return fieldsToAdd.length > 0
            ? [...this.getSoapSectionsWithFields(soapSections, new Set(fieldsToAdd)), ...sections]
            : sections;
    }

    public filterSections(sections: EntryTypeSectionData[]): EntryTypeSectionData[] {
        const usageInStaticSectionFiledNames = new Set(this.getSoapFieldNames());
        return this.filterSectionsByExludeFieldSet(sections, usageInStaticSectionFiledNames);
    }

    public filterSoapSections(sections: EntryTypeSectionData[]): EntryTypeSectionData[] {
        return this.filterSectionsByExludeFieldSet(sections, this.exludeInSoapFieldNames);
    }

    public filterSectionsByExludeFieldSet(
        sections: EntryTypeSectionData[],
        excludeFieldsSet: Set<string>,
    ): EntryTypeSectionData[] {
        return sections
            .map(section => ({
                ...section,
                blocks: section.blocks
                    .map(({ fields, ...block }) => ({
                        ...block,
                        fields: fields.filter(field => !excludeFieldsSet.has(field.name)),
                    }))
                    .filter(({ fields, id }) => id !== ENTRY_MEDICAL_CASE_BLOCK_ID && fields.length > 0),
            }))
            .filter(({ blocks }) => blocks.length > 0);
    }

    public getSoapFieldNames(): (EntryTypeFieldName | string)[] {
        const soapSections = toArrayFromIterable(store.entry.soapEntryType).flatMap(
            entryType => entryType.data?.sections ?? [],
        );
        const soapBlocks = soapSections.flatMap(section => section.blocks ?? []);
        return soapBlocks.flatMap(block => block.fields ?? []).map(field => field.name);
    }

    private getSoapSectionsWithFields(
        sections: EntryTypeSectionData[],
        fieldsNames: Set<string>,
    ): EntryTypeSectionData[] {
        return sections
            .map(({ blocks, ...section }) => ({
                ...section,
                blocks: blocks.map(({ fields, ...block }) => ({
                    ...block,
                    fields: fields.filter(({ name }) => fieldsNames.has(name)),
                })),
            }))
            .filter(section => section.blocks.some(({ fields }) => fields.length > 0));
    }
}

export const staticEntryTypeService = new StaticEntryTypeService();
